/* eslint-disable @typescript-eslint/camelcase */
import { FormControlLabel, Grid, makeStyles, Switch } from '@material-ui/core';
import DatePicker from 'components/DatePicker';
import { Formik } from 'formik';
import i18n from 'i18n';
import { FormPreparationServerProps } from 'modules/environments/interfaces';
import React from 'react';
import GenericForm from 'skyone-lib/lib/GenericForm';
import { applyI18n, formatDateToISO } from 'utils/helpers';
import Button from 'components/Button';
import Loading from 'components/Loading';
import moment from 'moment';
import formStyles from '../../../Form/styles';
import screenFields from './formFields';
import validationSchema from './schema';

const useStyles = makeStyles(formStyles);

const initial: FormPreparationServerProps = {
  instance_id: '',
  stop_time: moment().add(2, 'hours'),
  disable_auto_stop: false,
  no_stop: false,
};

export interface Props {
  onCancel: () => void;
  onSubmit: (values: FormPreparationServerProps) => void;
  initialValues?: FormPreparationServerProps;
  isLoading: boolean;
}

export default function FormPreparationServer({
  onCancel,
  onSubmit,
  initialValues = initial,
  isLoading,
}: Props) {
  const classes = useStyles();

  const handleSubmit = (values: FormPreparationServerProps) => {
    onSubmit({
      ...values,
      stop_time: formatDateToISO(values.stop_time),
    });
  };

  return (
    <Grid item container data-testid={'autosky-formpreparationserver'}>
      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
        enableReinitialize
      >
        {({ setFieldValue, submitForm, errors, values }) => (
          <Grid container spacing={2}>
            <GenericForm
              fields={values}
              screenFields={applyI18n(screenFields, i18n.language)}
              setFieldValue={setFieldValue}
              errors={errors}
            />

            {!values.disable_auto_stop && (
              <Grid item xs={12}>
                <DatePicker
                  id="stop_time"
                  type="datetime"
                  label={i18n.t('SCHEDULED_STOP_DATETIME_KEY')}
                  keyValue="stop_time"
                  value={values.stop_time}
                  setFieldsValue={setFieldValue}
                />
              </Grid>
            )}

            <Grid item xs={12}>
              <FormControlLabel
                classes={{ label: classes.switchLabel }}
                control={
                  <Switch
                    size="small"
                    id="toggle-scheduled-stop"
                    checked={values.disable_auto_stop}
                    onChange={(
                      event: React.ChangeEvent<HTMLInputElement>,
                      checked: boolean
                    ) => setFieldValue('disable_auto_stop', checked)}
                    classes={{ colorSecondary: classes.switchChecked }}
                  />
                }
                label={i18n.t('DISABLE_SCHEDULED_STOP_KEY')}
              />
            </Grid>

            <Grid item xs={12}>
              <FormControlLabel
                classes={{ label: classes.switchLabel }}
                control={
                  <Switch
                    id="toggle-generate-images"
                    checked={values.no_stop}
                    onChange={(
                      event: React.ChangeEvent<HTMLInputElement>,
                      checked: boolean
                    ) => setFieldValue('no_stop', checked)}
                    classes={{ colorSecondary: classes.switchChecked }}
                    size="small"
                  />
                }
                label={i18n.t('GENERATE_IMAGES_STOP_KEY')}
              />
            </Grid>

            <Grid container justify="flex-end" className={classes.buttonsBase}>
              <Grid container className={classes.buttonsAlign}>
                <Button
                  id="submit-button"
                  onClick={submitForm}
                  submitButton
                  disabled={isLoading}
                >
                  {isLoading ? <Loading size={15} /> : i18n.t('ADD_KEY')}
                </Button>
                <Button
                  id="cancel-button"
                  variant="outlined"
                  onClick={onCancel}
                >
                  {i18n.t('CANCEL_KEY')}
                </Button>
              </Grid>
            </Grid>
          </Grid>
        )}
      </Formik>
    </Grid>
  );
}
